import { useState } from "react";
import type { CrmLead } from "@/types/crm";
import { LeadCard } from "@/components/booking/LeadCard";
import type { KanbanStageId } from "@/components/booking/LeadsKanbanBoard";

type KanbanColumnProps = {
  stageId: KanbanStageId;
  title: string;
  description: string;
  leads: CrmLead[];
  accentClass: string;
  onLeadSelect: (lead: CrmLead) => void;
  onDropStage: (stage: KanbanStageId) => void;
  onDragStart: (leadId: number) => void;
  onDragEnd: () => void;
  canDragLead?: (lead: CrmLead) => boolean;
  renderActions?: (lead: CrmLead) => React.ReactNode;
  selectedLeadIds?: number[];
  onToggleLeadSelection?: (lead: CrmLead) => void;
  onEditLead?: (lead: CrmLead) => void;
};

export function KanbanColumn({
  stageId,
  title,
  description,
  leads,
  accentClass,
  onLeadSelect,
  onDropStage,
  onDragStart,
  onDragEnd,
  canDragLead,
  renderActions,
  selectedLeadIds,
  onToggleLeadSelection,
  onEditLead,
}: KanbanColumnProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <section
      className={`flex w-[240px] flex-none flex-col gap-3 rounded-2xl border border-border bg-background/70 p-3 transition snap-start sm:w-[270px] xl:w-[300px] ${
        isDragOver ? "border-amber-400/70 bg-background/40" : ""
      }`}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes("text/plain")) return;
        event.preventDefault();
      }}
      onDragEnter={(event) => {
        if (!event.dataTransfer.types.includes("text/plain")) return;
        event.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragOver(false);
        onDropStage(stageId);
      }}
    >
      <header>
        <p className={`text-xs font-semibold uppercase tracking-wide ${accentClass}`}>{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </header>
      <div className="space-y-2">
        {leads.length ? (
          leads.map((lead) => (
        <LeadCard
          key={lead.id}
          lead={lead}
          onClick={() => onLeadSelect(lead)}
          draggable={canDragLead ? canDragLead(lead) : true}
          onDragStart={() => onDragStart(lead.id)}
          onDragEnd={onDragEnd}
          actions={renderActions?.(lead)}
          selectable
          selected={Boolean(selectedLeadIds?.includes(lead.id))}
          onToggleSelect={() => onToggleLeadSelection?.(lead)}
          onEdit={onEditLead ? () => onEditLead(lead) : undefined}
        />
          ))
        ) : (
          <p className="rounded-xl border border-dashed border-border/80 bg-background/30 px-4 py-6 text-center text-xs text-muted-foreground">
            No leads in this stage yet.
          </p>
        )}
      </div>
    </section>
  );
}
